import { useEffect, useState, type ReactNode } from "react";
import {
  EXTRACT_DEVICE_KINDS,
  EXTRACT_DEVICE_LABELS,
  continuousExtractM3h,
  type ExtractDevice,
  type RadiatorSpace,
} from "@sts/schemas";
import { radiatorFit } from "@sts/calc";
import type { PhotoEvidence } from "../api/capture-types.js";
import type { RoomNotesView } from "../api/types.js";
import { ApiError } from "../api/client.js";
import { usePortal } from "../app/context.js";
import { PhotoPicker } from "../screens/SiteSurvey.jsx";

/**
 * A room's own record (migrations 0056, 0057), kept by the room's scan id so it
 * stays with the room through every re-conversion:
 *
 *   - photos, picked from the job's — those whose note names the room first;
 *   - notes;
 *   - extract fans and cooker hoods — a continuous one sets the room's
 *     ventilation loss when it takes out more than the air changes;
 *   - radiator spaces — with the largest radiator that fits each, and the
 *     smallest that keeps the room warm at the design flow temperature.
 */

interface Figures {
  readonly loadW: number | null;
  readonly roomTempC: number | null;
  readonly ventilationBasis?: "air-changes" | "continuous-extract";
  readonly ventilationAirM3h?: number;
  readonly flowC: number;
  readonly returnC: number;
  readonly flowAssumed: boolean;
}

export function RoomNotes({
  surveyId,
  roomRef,
  roomName,
  heatLossRoomId,
  photos,
  canEdit,
  revision = 0,
  onFiguresChanged,
}: {
  readonly surveyId: string;
  readonly roomRef: string;
  readonly roomName: string;
  /** The heat-loss room behind this scanned room, for its load and temperature. */
  readonly heatLossRoomId?: string;
  readonly photos: readonly PhotoEvidence[];
  readonly canEdit: boolean;
  readonly revision?: number;
  /** An extract fan changed the heat loss. */
  readonly onFiguresChanged?: () => void;
}): ReactNode {
  const { client } = usePortal();
  const [record, setRecord] = useState<RoomNotesView | null>(null);
  const [draft, setDraft] = useState("");
  const [devices, setDevices] = useState<ExtractDevice[]>([]);
  const [spaces, setSpaces] = useState<RadiatorSpace[]>([]);
  const [figures, setFigures] = useState<Figures | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let live = true;
    setLoaded(false);
    client
      .roomNotes(surveyId)
      .then((all) => {
        if (!live) return;
        const mine = all[roomRef] ?? null;
        setRecord(mine);
        setDraft(mine?.notes ?? "");
        setDevices([...(mine?.extractDevices ?? [])]);
        setSpaces([...(mine?.radiatorSpaces ?? [])]);
        setLoaded(true);
      })
      // An API that predates room records: leave the room as it was.
      .catch(() => live && setLoaded(false));
    return () => {
      live = false;
    };
  }, [client, surveyId, roomRef]);

  useEffect(() => {
    let live = true;
    Promise.allSettled([client.heatLoss(surveyId), client.designCheck(surveyId)]).then(([hl, dc]) => {
      if (!live) return;
      const rl = hl.status === "fulfilled" && heatLossRoomId ? hl.value.heatLoss.rooms.find((r) => r.roomId === heatLossRoomId) : undefined;
      const ctx = dc.status === "fulfilled" ? dc.value.context : null;
      const flowC = ctx?.designFlowTempC ?? 45;
      const deltaT = ctx?.systemDeltaTK ?? 5;
      setFigures({
        loadW: rl ? rl.totalW : null,
        roomTempC: rl ? rl.designTempC : null,
        ...(rl?.ventilationBasis ? { ventilationBasis: rl.ventilationBasis } : {}),
        ...(rl?.ventilationAirM3h !== undefined ? { ventilationAirM3h: rl.ventilationAirM3h } : {}),
        flowC,
        returnC: flowC - deltaT,
        flowAssumed: ctx?.designFlowTempC == null,
      });
    });
    return () => {
      live = false;
    };
  }, [client, surveyId, heatLossRoomId, revision]);

  if (!loaded) return null;

  const save = (input: { notes?: string; photoIds?: readonly string[]; extractDevices?: readonly ExtractDevice[]; radiatorSpaces?: readonly RadiatorSpace[] }): void => {
    setError(null);
    client
      .setRoomNotes(surveyId, roomRef, input)
      .then((next) => {
        setRecord(next);
        if (input.notes !== undefined) setDraft(next.notes);
        if (input.extractDevices !== undefined) onFiguresChanged?.();
      })
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : "Not saved. Check the connection and try again."));
  };
  const saveDevices = (next: ExtractDevice[]): void => {
    setDevices(next);
    save({ extractDevices: next.filter((d) => d.flowM3h >= 1) });
  };
  const saveSpaces = (next: RadiatorSpace[]): void => {
    setSpaces(next);
    save({ radiatorSpaces: next.filter((s) => s.widthMm >= 200 && s.heightMm >= 150) });
  };

  const continuous = continuousExtractM3h(devices);
  const fit =
    figures && figures.loadW !== null && figures.roomTempC !== null && spaces.some((s) => s.widthMm >= 200 && s.heightMm >= 150)
      ? radiatorFit({
          spaces: spaces.filter((s) => s.widthMm >= 200 && s.heightMm >= 150),
          loadW: figures.loadW,
          roomTempC: figures.roomTempC,
          flowC: figures.flowC,
          returnC: figures.returnC,
        })
      : null;
  const num = (v: string): number => Number(v.replace(",", ".")) || 0;

  return (
    <section className="room-notes" data-testid="room-notes" aria-label={`More about ${roomName}`}>
      <h3>Radiator spaces</h3>
      <p className="muted small">Where a radiator can go and how big — the room's radiators are sized to fit.</p>
      {spaces.map((s, i) => {
        const result = fit?.spaces.find((f) => f.widthMm === s.widthMm && f.heightMm === s.heightMm && (f.label ?? "") === (s.label ?? ""));
        return (
          <div key={i} className="room-notes__row" data-testid="radiator-space">
            <input
              aria-label={`Space ${i + 1}: where`}
              placeholder="Under the window"
              value={s.label ?? ""}
              disabled={!canEdit}
              maxLength={80}
              onChange={(e) => setSpaces(spaces.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))}
              onBlur={() => saveSpaces(spaces)}
            />
            <label className="room-notes__mm">
              <input
                aria-label={`Space ${i + 1}: width in mm`}
                inputMode="numeric"
                value={s.widthMm || ""}
                disabled={!canEdit}
                onChange={(e) => setSpaces(spaces.map((x, j) => (j === i ? { ...x, widthMm: num(e.target.value) } : x)))}
                onBlur={() => saveSpaces(spaces)}
              />
              <span className="muted">wide</span>
            </label>
            <label className="room-notes__mm">
              <input
                aria-label={`Space ${i + 1}: height in mm`}
                inputMode="numeric"
                value={s.heightMm || ""}
                disabled={!canEdit}
                onChange={(e) => setSpaces(spaces.map((x, j) => (j === i ? { ...x, heightMm: num(e.target.value) } : x)))}
                onBlur={() => saveSpaces(spaces)}
              />
              <span className="muted">high, mm</span>
            </label>
            {canEdit ? (
              <button type="button" className="floor-plan__link" onClick={() => saveSpaces(spaces.filter((_, j) => j !== i))}>
                Remove
              </button>
            ) : null}
            {result ? (
              <div className="room-notes__fit small" data-testid="radiator-fit">
                {result.largest ? (
                  <span>
                    Largest that fits: <b>{result.largest.label}</b> — {result.largest.outputW} W.{" "}
                  </span>
                ) : (
                  <span>No radiator in the range fits this space. </span>
                )}
                {result.smallestThatCovers ? (
                  <span>
                    Smallest that does its share ({result.shareW} W): <b>{result.smallestThatCovers.label}</b> — {result.smallestThatCovers.outputW} W.
                  </span>
                ) : result.largest ? (
                  <span className="room-notes__short">Even the largest gives less than its share ({result.shareW} W).</span>
                ) : null}
              </div>
            ) : null}
          </div>
        );
      })}
      {canEdit ? (
        <button type="button" className="site-survey__chip" onClick={() => setSpaces([...spaces, { widthMm: 0, heightMm: 0 }])}>
          + Add a radiator space
        </button>
      ) : null}
      {fit && figures ? (
        <p className={`small ${fit.enough ? "room-notes__ok" : "room-notes__short"}`} data-testid="radiator-fit-summary">
          At {figures.flowC} °C flow{figures.flowAssumed ? " (assumed — set it in the design check)" : ""}: these spaces can give up to{" "}
          {fit.maxOutputW} W; the room needs {fit.loadW} W.{" "}
          {fit.enough ? "Enough." : "Not enough: add a space, or raise the flow temperature."} Allow for the valves either side.
        </p>
      ) : null}

      <h3>Extract fans</h3>
      <p className="muted small">
        A fan that runs all the time takes out air that comes back in cold. When it takes out more than the room's air changes, it
        sets the room's ventilation loss. One that only runs on demand does not.
      </p>
      {devices.map((d, i) => (
        <div key={i} className="room-notes__row" data-testid="extract-device">
          <select
            aria-label={`Extract ${i + 1}: kind`}
            value={d.kind}
            disabled={!canEdit}
            onChange={(e) => saveDevices(devices.map((x, j) => (j === i ? { ...x, kind: e.target.value as ExtractDevice["kind"] } : x)))}
          >
            {EXTRACT_DEVICE_KINDS.map((k) => (
              <option key={k} value={k}>
                {EXTRACT_DEVICE_LABELS[k]}
              </option>
            ))}
          </select>
          <label className="room-notes__mm">
            <input
              aria-label={`Extract ${i + 1}: air flow in cubic metres an hour`}
              inputMode="decimal"
              value={d.flowM3h || ""}
              disabled={!canEdit}
              onChange={(e) => setDevices(devices.map((x, j) => (j === i ? { ...x, flowM3h: num(e.target.value) } : x)))}
              onBlur={() => saveDevices(devices)}
            />
            <span className="muted">m³/h</span>
          </label>
          <label className="room-notes__check">
            <input
              type="checkbox"
              checked={d.continuous}
              disabled={!canEdit}
              onChange={(e) => saveDevices(devices.map((x, j) => (j === i ? { ...x, continuous: e.target.checked } : x)))}
            />
            Runs all the time
          </label>
          {canEdit ? (
            <button type="button" className="floor-plan__link" onClick={() => saveDevices(devices.filter((_, j) => j !== i))}>
              Remove
            </button>
          ) : null}
        </div>
      ))}
      {canEdit ? (
        <button
          type="button"
          className="site-survey__chip"
          onClick={() => setDevices([...devices, { kind: "extract-fan", flowM3h: 0, continuous: false }])}
        >
          + Add an extract fan
        </button>
      ) : null}
      {continuous > 0 && figures ? (
        <p className="small" data-testid="extract-effect">
          Continuous extract {continuous} m³/h —{" "}
          {figures.ventilationBasis === "continuous-extract"
            ? `sets this room's ventilation loss (${figures.ventilationAirM3h ?? continuous} m³/h).`
            : "less than the room's air changes already take out, so the ventilation loss is unchanged."}
        </p>
      ) : null}

      <h3>Photos &amp; notes</h3>
      <PhotoPicker
        field={{ id: "room_photos", label: `Photos of ${roomName}`, kind: "photos", photoHints: [roomName.toLowerCase()] }}
        value={record?.photoIds ?? []}
        disabled={!canEdit}
        photos={photos}
        onAnswer={(v) => save({ photoIds: Array.isArray(v) ? (v as string[]) : [] })}
      />
      <textarea
        className="site-survey__text"
        aria-label={`Notes for ${roomName}`}
        rows={2}
        placeholder={canEdit ? "Anything about this room — access, where a radiator can and cannot go…" : ""}
        value={draft}
        disabled={!canEdit}
        maxLength={4000}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          if (draft !== (record?.notes ?? "")) save({ notes: draft });
        }}
      />
      {record?.updatedBy ? <p className="muted small">Last changed by {record.updatedBy.name}</p> : null}
      {error ? (
        <p className="notice error" role="alert">
          {error}
        </p>
      ) : null}
    </section>
  );
}
